import { useState, useMemo } from 'react';
import { Avatar, Box, AvatarGroup, CircularProgress } from '@mui/material';
import { format } from 'date-fns';
import { Lock, LockOpen, PersonAdd } from '@mui/icons-material';
import { formatPriceByLocale, parseToDate } from '../../lib/utils';
import useTimetableService from '../../services/TimetableService';

const { apiEnv } = window.runtimeConfig;

type Player = {
    id: number;
    name: string;
    img: {
        uri: string;
    };
};

type Booking = {
    id: number;
    name?: string;
};

type Capacity = {
    free: number;
    total: number;
};

type PriceShape = {
    currency?: string;
    locale?: string;
};

type Piece = {
    ini: string;
    end: string;
    duration: number;
    mark: string;
    title?: string;
    price?: number;
    priceShape?: PriceShape;
    capacity?: Capacity;
    bookings?: Booking[];
    percentagePaid?: number;
    isGame?: boolean;
    isGamePublic?: boolean;
    fixed?: boolean;
    notes?: string;
    players?: Player[];
    privilegesData?: unknown;
    timetableRates?: unknown;
};

type Facility = {
    id?: number;
    name?: string;
    status?: string;
};

type Column = {
    facility: Facility;
    pieces: Piece[];
};

type Props = {
    column: Column;
    piece: Piece;
    columnIndex?: number;
    timeSlots?: string[];
    onOpenPiece: (column: Column, piece: Piece) => void;
};

/**
 * Timetable piece with optional CSS Grid positioning
 * When columnIndex and timeSlots are provided, uses CSS Grid positioning
 * Otherwise falls back to height-based positioning
 */
export default function SttTimetablePiece({
    column,
    piece,
    columnIndex,
    timeSlots,
    onOpenPiece,
}: Props): React.ReactElement | null {
    const {
        useGetTimetableOccupations,
        useGetTimetableRates,
        useGetTimetablePrivileges,
    } = useTimetableService();

    const isGameClass = useMemo(
        () => piece.isGame && (piece.mark === 'FREE' || piece.mark === 'FULL'),
        [piece.isGame, piece.mark],
    );

    const capacityClass = useMemo(() => {
        if (!piece.capacity) return '';
        if (piece.capacity.free === 0) return 'full';
        if (piece.capacity.free < piece.capacity.total) return 'taken';
        return '';
    }, [piece.capacity]);

    const facilityId = column?.facility?.id ?? 0;

    const [isLoading, setIsLoading] = useState(false);

    // Use CSS Grid positioning when timeSlots and columnIndex are provided
    const useGridLayout =
        timeSlots !== undefined &&
        columnIndex !== undefined &&
        timeSlots.length > 0;

    // Calculate positioning style based on layout mode
    const positionStyle = useMemo(() => {
        if (useGridLayout && timeSlots && columnIndex !== undefined) {
            const iniIndex = timeSlots.indexOf(piece.ini);
            const endIndex = timeSlots.indexOf(piece.end);

            if (iniIndex === -1 || endIndex === -1) {
                return {
                    gridColumn: columnIndex + 1,
                    height: Math.max(piece.duration * 2, 30),
                };
            }

            return {
                gridColumn: columnIndex + 1,
                gridRow: `${iniIndex + 1} / ${endIndex + 1}`,
            };
        }
        return {
            height: Math.max(piece.duration * 2, 30),
        };
    }, [
        piece.ini,
        piece.end,
        piece.duration,
        timeSlots,
        columnIndex,
        useGridLayout,
    ]);

    const { refetch: occupationsRefetch } = useGetTimetableOccupations(
        {
            dateIni: piece?.ini,
            dateEnd: piece?.end,
            fieldId: facilityId,
        },
        {
            enabled: false,
        },
    );

    const { refetch: ratesRefetch } = useGetTimetableRates(
        {
            dateIni: piece?.ini,
            dateEnd: piece?.end,
            fieldId: facilityId,
        },
        {
            enabled: false,
        },
    );

    const { refetch: privilegesRefetch } = useGetTimetablePrivileges(
        {
            dateIni: piece?.ini,
            dateEnd: piece?.end,
            fieldId: facilityId,
        },
        {
            enabled: false,
        },
    );

    /**
     * Load Piece info
     */
    async function handleOpenPiece() {
        setIsLoading(true);
        try {
            const { data: timetableRates } = await ratesRefetch();
            const { data: occupationsData } = await occupationsRefetch();
            const { data: privilegesData } = await privilegesRefetch();

            onOpenPiece(column, {
                ...piece,
                privilegesData,
                bookings: occupationsData?.rows,
                timetableRates,
            });
        } finally {
            setIsLoading(false);
        }
    }

    // Parse the date for formatting
    const parsedIni = parseToDate(piece.ini);
    const formattedTime = parsedIni ? format(parsedIni, 'HH:mm') : '';

    return (
        <Box
            id="timetable-piece"
            className={`piece ${isGameClass ? 'GAME' : piece.mark} justify-center items-center`}
            style={positionStyle}
            onClick={() => {
                if (
                    piece.mark !== 'NO_SET' &&
                    piece.mark !== 'NO_RR' &&
                    !isLoading
                ) {
                    handleOpenPiece();
                }
            }}
        >
            {isLoading ? (
                <Box className="flex items-center justify-center h-full">
                    <CircularProgress size={16} />
                </Box>
            ) : (
                <>
                    <Box className="time">{formattedTime}</Box>

                    {piece?.capacity?.total === 1 &&
                        piece?.capacity.free === 0 && (
                            <Box className="time">
                                {piece?.bookings?.[0]?.name || ''}
                            </Box>
                        )}

                    {/* --- Porcentage payment --- */}
                    {piece.mark === 'RENT' && !piece.capacity && (
                        <div className="paymentBar">
                            <div
                                style={{ width: `${piece.percentagePaid}%` }}
                            />
                        </div>
                    )}

                    {piece.mark === 'RENT' &&
                        !!piece.isGame &&
                        !!piece.capacity &&
                        (piece.percentagePaid ?? 0) > 0 &&
                        (piece.percentagePaid ?? 0) < 100 && (
                            <div className="paymentBar">
                                <div
                                    style={{
                                        width: `${piece.percentagePaid}%`,
                                    }}
                                />
                            </div>
                        )}

                    {piece?.title ? (
                        <Box
                            className={piece.isGame ? 'gameConcept' : 'concept'}
                        >
                            {piece.title}
                        </Box>
                    ) : null}

                    {/* --- Price --- */}
                    {piece.mark === 'FREE' && (piece.price ?? 0) > 0 && (
                        <Box className="price">
                            {formatPriceByLocale(
                                piece.price,
                                piece?.priceShape?.currency,
                                piece?.priceShape?.locale,
                            )}
                        </Box>
                    )}

                    {/* --- Capacity --- */}
                    {!!piece.capacity && piece.capacity.total > 1 && (
                        <Box className={`capacity ${capacityClass}`}>
                            {piece.capacity.free} / {piece.capacity.total}
                        </Box>
                    )}

                    {/* --- Flags (top-left corner) --- */}
                    {!!piece.fixed && piece.mark === 'RENT' && (
                        <div className="absolute top-0 left-0 w-3.5 h-3.5 bg-gray-500 text-white text-[8px] font-semibold flex items-center justify-center z-10">
                            F
                        </div>
                    )}
                    {!!piece.notes && (
                        <div
                            className={`absolute top-0 ${piece.fixed && piece.mark === 'RENT' ? 'left-3.5' : 'left-0'} w-3.5 h-3.5 bg-gray-500 text-white text-[8px] font-semibold flex items-center justify-center z-10`}
                        >
                            N
                        </div>
                    )}

                    {piece.isGame ? (
                        <>
                            <Box className="visibility">
                                {piece.isGamePublic ? (
                                    <LockOpen className="public" />
                                ) : (
                                    <Lock className="private" />
                                )}
                            </Box>

                            <Box className="players">
                                <AvatarGroup max={3}>
                                    {piece.players?.map((player) => (
                                        <Avatar
                                            key={player.id}
                                            src={`${apiEnv.url}/${player.img.uri}`}
                                            alt={player.name}
                                            className="playerAvatar"
                                        />
                                    ))}

                                    {!!piece.capacity && piece.capacity.free > 0
                                        ? Array.from(
                                              {
                                                  length: piece.capacity.free,
                                              },
                                              (_, i) => i,
                                          ).map((index) => (
                                              <Avatar
                                                  key={`Empty-${index}`}
                                                  className="emptyAvatar"
                                              >
                                                  <PersonAdd className="w-[12px]" />
                                              </Avatar>
                                          ))
                                        : undefined}
                                </AvatarGroup>
                            </Box>
                        </>
                    ) : undefined}
                </>
            )}
        </Box>
    );
}
